<template>
  <div class="h-full flex flex-col" v-if="education !== null">
    <h1 class="text-4xl mb-5">
      {{ education.title }} -
      <small>{{ education.type_string }}</small>
    </h1>
    <div id="page" @scroll="$bus.$emit('scroll')" class="content flex-grow">
      <div id="edu-description" class="section">
        <div class="scale-grid flex max-h-50 flex-row flex-wrap items-start">
          <tile
            class="w-full full"
            :key="education.banner_image.url"
            :image="education.banner_image.url"
            :scale="false"
            :cursor="false"
            :info="false"
            move-on-mouse-over
          />
        </div>
        <div class="scale-grid flex max-h-50 flex-row flex-wrap">
          <tile
            :class="{
              'w-full': mobile,
              'w-1/3': !mobile,
              'order-last': mobile,
            }"
            :margin="false"
            :scale="false"
            :info="false"
          >
            <image-slides
              slot="tile-content"
              class="w-full h-full overflow-hidden"
              :images="education.promo_images"
              :lightbox="(index) => $refs.imageLigtbox.open(index)"
            ></image-slides>
          </tile>
          <div :class="[mobile ? 'w-full mb-6' : 'w-2/3', 'px-3 flex']">
            <div class="tile-content">
              <p class="fill-content pb-2">
                {{ education.description }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <ImageLightbox ref="imageLigtbox" :images="education.promo_images" />

      <div id="edu-details" class="section">
        <div :class="['flex flex-row', mobile ? 'flex-wrap' : '']">
          <div :class="[mobile ? 'w-full mb-6' : 'w-1/3', 'list']">
            <div class="flex flex-row">
              <icon name="Type" class="icon self-start" />
              <div class="flex-grow">
                <h2 class="text-2xl">Type</h2>
                <p>{{ education.short_description }}</p>
              </div>
            </div>
            <div class="flex flex-row">
              <icon name="Difficulty" class="icon self-start" />
              <div class="flex-grow">
                <h2 class="text-2xl">Difficulty</h2>
                <p>{{ education.difficulty_string }}</p>
              </div>
            </div>
            <div class="flex flex-row">
              <icon name="Language" class="icon self-start" />
              <div class="flex-grow">
                <h2 class="text-2xl">Language</h2>
                <p>{{ education.lang }}</p>
              </div>
            </div>
            <div class="flex flex-row">
              <icon name="Certificate" class="icon self-start" />
              <div class="flex-grow">
                <h2 class="text-2xl">Certificate</h2>
                <p>
                  {{ education.certificate ? 'Yes' : 'No' }}
                </p>
              </div>
            </div>
            <div class="flex flex-row">
              <icon name="Start" class="icon self-start" />
              <div class="flex-grow">
                <h2 class="text-2xl">Start</h2>
                <p>
                  {{ formatDate(education.dates[0].from) }}
                </p>
              </div>
            </div>
            <div class="flex flex-row">
              <icon name="Duration" class="icon self-start" />
              <div class="flex-grow">
                <h2 class="text-2xl">Duration</h2>
                <p>
                  {{ calculateDuration(education.dates) }}
                </p>
              </div>
            </div>
            <div class="flex flex-row">
              <icon name="Time" class="icon self-start" />
              <div class="flex-grow">
                <h2 class="text-2xl">Time</h2>
                <p>
                  {{ averageTime(education.average_time_from, education.average_time_to) }}
                </p>
              </div>
            </div>
            <div class="flex flex-row">
              <icon name="Location" class="icon self-start" />
              <div class="flex-grow">
                <h2 class="text-2xl">Location</h2>
                <p>
                  {{ educationLocation(education) }}
                </p>
              </div>
            </div>
            <div class="flex flex-row mb-0">
              <icon name="Price" class="icon self-start" />
              <div class="flex-grow">
                <h2 class="text-2xl">Price</h2>
                <p v-for="price in education.prices">
                  Until
                  {{ formatDate(price.valid_until) }}: €{{ price.amount / 100 }}
                </p>
              </div>
            </div>
          </div>
          <div :class="[mobile ? 'w-full' : 'w-2/3', 'flex flex-row']">
            <div class="list w-full">
              <div class="flex flex-row mb-16">
                <icon name="learn" class="icon self-start" />
                <div class="flex-grow">
                  <h2 class="text-2xl">What You Will Learn</h2>
                  <ul class="list-disc pl-5">
                    <template v-for="n in 10">
                      <li v-if="education.learnings[n - 1]">
                        {{ education.learnings[n - 1] }}
                      </li>
                      <br v-else />
                    </template>
                  </ul>
                </div>
              </div>
              <div class="flex flex-row mb-3">
                <icon name="Requirements" class="icon self-start" />
                <div class="flex-grow">
                  <h2 class="text-2xl">Requirements</h2>
                  <ul class="list-disc pl-5">
                    <template v-for="n in 7">
                      <li v-if="education.requirements[n - 1]">
                        {{ education.requirements[n - 1] }}
                      </li>
                      <br v-else />
                    </template>
                  </ul>
                </div>
              </div>
              <div class="flex flex-row">
                <icon name="Additional" class="icon self-start" />
                <div class="flex-grow">
                  <h2 class="text-2xl">Additional</h2>
                  <ul class="list-disc pl-5">
                    <template v-for="n in 6">
                      <li v-if="education.additional_info[n - 1]">
                        {{ education.additional_info[n - 1] }}
                      </li>
                      <br v-else />
                    </template>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="edu-schedule" class="section">
        <div class="scale-grid flex max-h-50 flex-row flex-wrap calendar">
          <div :class="[mobile ? 'w-full' : 'w-1/3', 'px-3']">
            <v-calendar
              :masks="masks"
              :from-date="dates.from"
              :to-date="dates.to"
              ref="calendar"
              :attributes="attributes"
              :available-dates="availableDates"
              is-expanded
              is-dark
              title-position="left"
            />
          </div>
          <div :class="[mobile ? 'w-full' : 'w-2/3', 'px-3 flex flex-col calendar-description']">
            <div class="flex-grow">
              <div v-for="(d, index) in education.dates" :class="[index + 1 !== education.dates.length ? 'mb-5' : '']">
                <p class="text-xl">
                  Day {{ index + 1 }}:
                  {{ educationDate(d) }}
                </p>
                <p>{{ d.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div id="edu-team">
        <template v-for="n in Math.ceil(team.length / 2)">
          <div
            class="scale-grid flex max-h-50 flex-row flex-wrap"
            v-for="(mem, index) in sliceTeam(n)"
            :key="`mem_${index}_${mem.id}`"
          >
            <div :class="['team-item', mobile ? 'flex-col flex-wrap' : '']" v-if="mem.m_type === 'member'">
              <tile
                v-if="mem.headshot_image"
                :class="['team-tile', mobile ? 'w-full' : 'w-1/3']"
                :image="mem.headshot_image.url"
                :info="false"
                @click="
                  $router.push({
                    name: 'team-detail',
                    params: { slug: mem.slug },
                  })
                "
              />
              <div :class="[mobile ? 'w-full' : 'w-2/3', 'px-3 flex flex-col pb-6']">
                <div class="tile-content h-full">
                  <div class="fill-content">
                    <h2 class="text-xl">{{ mem.name }} - {{ mem.role }}</h2>
                    <p class="mb-4">
                      {{ mem.country }}
                    </p>

                    <p>{{ mem.position }} @ DesignMorphine</p>
                    <p class="mb-4">
                      {{ mem.title }} @
                      {{ mem.company }}
                    </p>

                    <p>{{ mem.bio }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div :class="['team-item', mobile ? 'flex-col flex-wrap' : '']" v-if="mem.m_type === 'guest_member'">
              <tile
                v-if="mem.headshot"
                :class="['team-tile', mobile ? 'w-full' : 'w-1/3']"
                :image="mem.headshot.url"
                :info="false"
                @click="goTo(mem.url)"
              />
              <div :class="[mobile ? 'w-full' : 'w-2/3', 'px-3 flex flex-col pb-6']">
                <div class="tile-content h-full">
                  <div class="fill-content">
                    <h2 class="text-xl">{{ mem.name }} - {{ mem.role }}</h2>
                    <p class="mb-4">
                      {{ mem.country }}
                    </p>

                    <div class="mb-4">
                      <p v-for="i in mem.info">
                        <span v-if="i.name && i.place"> {{ i.name }} @ {{ i.place }} </span>
                      </p>
                    </div>

                    <p>{{ mem.bio }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div :class="['team-item', mobile ? 'flex-col flex-wrap' : '']" v-if="mem.m_type === 'guest_company'">
              <tile
                v-if="mem.logo"
                :class="['team-tile', mobile ? 'w-full' : 'w-1/3']"
                :image="mem.logo.url"
                :info="false"
                @click="goTo(mem.url)"
              />
              <div :class="[mobile ? 'w-full' : 'w-2/3', 'px-3 flex flex-col pb-6']">
                <div class="tile-content h-full">
                  <div class="fill-content pb-2">
                    <h2 class="text-xl">{{ mem.name }} - {{ mem.role }}</h2>
                    <p class="mb-4">
                      {{ mem.country }}
                    </p>

                    <p>{{ mem.bio }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div id="edu-recording">
        <div v-if="education.recording_video" class="">
          <div class="youtube-container mb-2">
            <youtube class="youtube-inner" :video-id="getVideoId(education.recording_video)" />
          </div>
        </div>
      </div>

      <div id="edu-results" class="section" v-if="education.result_images.length > 0">
        <div class="grid grid-cols-6 gap-3">
          <div v-for="(r, ind) in education.result_images" :class="ind in gridClasses ? gridClasses[ind] : ''">
            <tile
              :class="{
                'w-full': true,
                'cursor-pointer': true,
              }"
              :key="r.url"
              :image="r.url"
              :scale="false"
              :cursor="false"
              :info="false"
              :padding="false"
              :margin="false"
              @click="$refs.resultLigtbox.open(ind)"
            />
          </div>
        </div>
        <ImageLightbox ref="resultLigtbox" :images="education.result_images" />

        <template v-if="education.participants">
          <h2 class="mb-4 text-xl">Participants</h2>
          <p>{{ education.participants }}</p>
        </template>
      </div>
    </div>
    <button
      class="action-btn self-end w-full bg-white-25 hover:bg-dm py-2 rounded-full mb-0"
      @click.prevent="openJoin"
      v-if="!education.is_disabled"
    >
      Sign Up
    </button>
    <button v-else class="action-btn self-end w-full bg-white-25 py-2 rounded-full mb-0" disabled>
      Registration Ended
    </button>
  </div>
</template>

<script>
import SingleImage from '@components/details/SingleImage';
import ImageSlides from '@components/details/ImageSlides';
import Icon from '@components/Icon';
import Tile from '@components/Tile';
import moment from 'moment-timezone';
import ImageLightbox from '@components/ImageLightbox';

export default {
  name: 'education-details',
  components: { ImageSlides, SingleImage, Icon, Tile, ImageLightbox },
  data() {
    return {
      education: null,
      loading: true,
      masks: {
        weekdays: 'WW',
      },
      mobile: this.$store.state.screenSize === 1,
      gridClasses: {
        4: 'row-span-2 col-span-2',
        9: 'row-span-2 col-span-2',
        12: 'row-span-2 col-span-2',
        15: 'row-span-4 col-span-4',
        20: 'row-span-2 col-span-2',
        21: 'row-span-2 col-span-2',
        22: 'row-span-3 col-span-3',

        32: 'row-span-2 col-span-2',
        37: 'row-span-2 col-span-2',
        40: 'row-span-2 col-span-2',
        43: 'row-span-4 col-span-4',
        48: 'row-span-2 col-span-2',
        49: 'row-span-2 col-span-2',
        50: 'row-span-3 col-span-3',

        60: 'row-span-2 col-span-2',
        65: 'row-span-2 col-span-2',
        68: 'row-span-2 col-span-2',
        71: 'row-span-4 col-span-4',
        76: 'row-span-2 col-span-2',
        77: 'row-span-2 col-span-2',
        78: 'row-span-3 col-span-3',
      },
    };
  },
  watch: {
    '$store.state.screenSize': function (size) {
      this.mobile = size === 1;
    },
  },
  computed: {
    dates() {
      return {
        from: new Date(moment(this.education.dates[0].from).toDate()),
        to: new Date(moment(this.education.dates[this.education.dates.length - 1].from).toDate()),
      };
    },
    timer() {
      let first = moment(this.education.dates[0].from);
      let duration = moment.duration(first.diff(moment(moment().tz('UTC').format('YYYY-MM-DD HH:mm'))));

      if (duration.asDays() < 0) {
        return {
          days: '00',
          hours: '00',
          mins: '00',
        };
      }

      return {
        days: Math.floor(duration.asDays()),
        hours: duration.hours(),
        mins: duration.minutes(),
      };
    },
    team() {
      if (this.education !== null) {
        let t = this.education.members.map((m) => {
          m.m_type = 'member';
          return m;
        });
        t = t.concat(
          this.education.guest_members.map((m) => {
            m.m_type = 'guest_member';
            return m;
          }),
        );
        t = t.concat(
          this.education.guest_companies.map((m) => {
            m.m_type = 'guest_company';
            return m;
          }),
        );

        return t.sort((a, b) => a.order - b.order);
      }

      return false;
    },
    availableDates() {
      return this.education.dates.map((date) => {
        return new Date(moment(date.from).toDate());
      });
    },
    attributes() {
      return this.education.dates.map((date) => {
        return {
          highlight: true,
          dates: new Date(moment(date.from).toDate()),
          color: 'white',
        };
      });
    },
  },

  mounted() {
    this.getEducation(this.$route.params.slug)
      .then(({ data }) => {
        this.education = data;
        this.loading = false;

        if (this.education.result_images !== null && this.education.result_images.length > 0) {
          this.$store.state.educationResults = true;
        }
      })
      .catch((e) => {
        if (e.response.status === 404) {
          this.$router.push('/404');
        }
      });

    this.$bus.$on('prev', () => {
      this.$router.push({
        name: `education-detail`,
        params: { slug: this.education.nav.prev.slug },
      });
    });

    this.$bus.$on('next', () => {
      this.$router.push({
        name: `education-detail`,
        params: { slug: this.education.nav.next.slug },
      });
    });

    this.$bus.$on('back', () => {
      this.$router.push({
        name: this.education.is_archived ? 'archive' : 'education',
      });
    });

    this.$bus.$emit('scroll-start');
  },

  beforeDestroy() {
    this.$bus.$off('prev');
    this.$bus.$off('next');
    this.$bus.$off('back');
  },

  methods: {
    sliceTeam(index) {
      return this.team.slice((index - 1) * 2, (index - 1) * 2 + 2);
    },
    educationDate(date) {
      let to = moment(date.to);
      let from = moment(date.from);

      return `${from.format('MMMM DD, YYYY - dddd')} (${from.format('ha')} to ${to.format('ha')} UTC)`;
    },
    openJoin() {
      if (this.education.protected) {
        this.$modal.show('join', {
          url: this.education.payment_url,
          protected: this.education.protected,
          code: this.education.code,
          text: 'Continue to Registration',
        });
      } else {
        this.goTo(this.education.payment_url);
      }
    },
    getEducation(slug) {
      return window.axios.get(`/api/educations/${slug}?include=nav`);
    },
    educationLocation(education) {
      if (education.is_online) {
        return `${education.online_platform}, Online`;
      } else {
        return `${education.city}, ${education.country}`;
      }
    },
    averageTime(from, to) {
      return `${moment(from, 'HH:mm').format('ha')} to ${moment(to, 'HH:mm').format('ha')} UTC daily`;
    },
    calculateDuration(dates) {
      let hours = 0,
        days = 0;

      dates.forEach((d) => {
        days++;
        let diff = moment.duration(moment(d.to).diff(moment(d.from)));
        hours += diff.asHours();
      });

      if (days === 1) {
        return `${hours} hours on a single day`;
      }

      return `${hours} hours over ${days} days`;
    },
    getVideoId(url) {
      if (!url) {
        return '';
      }

      return this.$youtube.getIdFromURL(url.split('/').pop());
    },
  },
};
</script>
<style scoped lang="scss">
@use 'sass:math';

#edu-details {
  margin-top: -8px;
}

#router-cont {
  padding-bottom: 7.5rem !important;
}

.team-item {
  width: 100%;
  display: flex;
  flex-direction: row;

  .team-tile {
    cursor: pointer;
  }
}

.timer {
  padding: 1rem;
  background: rgba(0, 0, 0, 0.3);
  height: 100%;
}

.youtube-container {
  width: 100%;
  position: relative;
}

.youtube-container:before {
  display: block;
  content: '';
  width: 100%;
  padding-top: math.div(9, 16) * 100%;
}

.youtube-inner {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
}

@media screen and (max-width: 768px) {
  .calendar {
    flex-direction: column-reverse;
  }

  .calendar-description {
    margin-bottom: 12px;
  }
}
</style>
